import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { 
  ArrowLeft, Play, Pause, Trash2, TrendingUp, TrendingDown,
  Eye, ShoppingCart, Target, RefreshCw, Plus, Check, X
} from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import AdminLayout from "./AdminLayout";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ABTestMonitor = () => {
  const { testId } = useParams();
  const navigate = useNavigate();
  const [test, setTest] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tests, setTests] = useState([]);
  const [showCreate, setShowCreate] = useState(false);
  const [newTest, setNewTest] = useState({
    name: "",
    page_slug: "",
    traffic_split: 50
  });
  const [landingPages, setLandingPages] = useState([]);

  useEffect(() => {
    if (testId) {
      fetchTest();
    } else {
      fetchAllTests();
    }
    fetchLandingPages();
  }, [testId]);

  const fetchTest = async () => {
    try {
      const res = await axios.get(`${API}/admin/ab-test/${testId}`);
      setTest(res.data);
    } catch (err) {
      toast.error("Failed to load A/B test");
      navigate("/admin/ab-tests");
    } finally {
      setLoading(false);
    }
  };

  const fetchAllTests = async () => {
    try {
      const res = await axios.get(`${API}/admin/ab-tests`);
      setTests(res.data);
    } catch (err) {
      toast.error("Failed to load A/B tests");
    } finally {
      setLoading(false);
    }
  };

  const fetchLandingPages = async () => {
    try {
      const res = await axios.get(`${API}/admin/event-landing-pages`);
      setLandingPages(res.data);
    } catch (err) {
      console.log("Could not load landing pages");
    }
  };

  const createTest = async () => {
    if (!newTest.name || !newTest.page_slug) {
      toast.error("Name and page are required");
      return;
    }
    try {
      await axios.post(`${API}/admin/ab-test`, newTest);
      toast.success("A/B test created!");
      setShowCreate(false);
      setNewTest({ name: "", page_slug: "", traffic_split: 50 });
      fetchAllTests();
    } catch (err) {
      toast.error("Failed to create test");
    }
  };

  const toggleTestStatus = async (id, currentStatus) => {
    try {
      await axios.put(`${API}/admin/ab-test/${id}`, {
        status: currentStatus === "active" ? "paused" : "active"
      });
      if (testId) {
        fetchTest();
      } else {
        fetchAllTests();
      }
      toast.success(`Test ${currentStatus === "active" ? "paused" : "activated"}`);
    } catch (err) {
      toast.error("Failed to update test");
    }
  };

  const deleteTest = async (id) => {
    if (!confirm("Delete this A/B test?")) return;
    try {
      await axios.delete(`${API}/admin/ab-test/${id}`);
      toast.success("Test deleted");
      if (testId) {
        navigate("/admin/ab-tests");
      } else {
        fetchAllTests();
      }
    } catch (err) {
      toast.error("Failed to delete test");
    }
  };

  // Single test view
  if (testId && test) {
    const variantA = test.results?.variant_a || { views: 0, conversions: 0, rate: 0 };
    const variantB = test.results?.variant_b || { views: 0, conversions: 0, rate: 0 };
    const winner = variantA.rate > variantB.rate ? "A" : variantB.rate > variantA.rate ? "B" : null;

    return (
      <AdminLayout>
        <div className="p-6 lg:p-8">
          {/* Header */}
          <div className="flex items-center justify-between mb-8">
            <div className="flex items-center gap-4">
              <Link 
                to="/admin/ab-tests" 
                className="p-2 text-[#A1A1AA] hover:text-[#F5F5F0] transition-colors"
              >
                <ArrowLeft className="w-5 h-5" />
              </Link>
              <div>
                <h1 className="text-2xl font-bold text-[#F5F5F0]">{test.name}</h1>
                <p className="text-[#A1A1AA]">/{test.page_slug}</p>
              </div>
            </div>
            
            <div className="flex items-center gap-3">
              <span className={`px-3 py-1 rounded text-sm font-medium ${
                test.status === "active" 
                  ? "bg-green-500/20 text-green-400" 
                  : "bg-yellow-500/20 text-yellow-400"
              }`}>
                {test.status}
              </span>
              <button
                onClick={() => toggleTestStatus(test.id, test.status)}
                className="p-2 bg-[#27272A] text-[#F5F5F0] hover:bg-[#3F3F46] transition-colors rounded"
              >
                {test.status === "active" ? <Pause className="w-5 h-5" /> : <Play className="w-5 h-5" />}
              </button>
              <button
                onClick={() => deleteTest(test.id)}
                className="p-2 bg-red-500/10 text-red-400 hover:bg-red-500/20 transition-colors rounded"
              >
                <Trash2 className="w-5 h-5" />
              </button>
            </div>
          </div>

          {/* Results Comparison */}
          <div className="grid md:grid-cols-2 gap-6 mb-8">
            {/* Variant A */}
            <div className={`bg-[#121212] border-2 rounded-lg p-6 ${
              winner === "A" ? "border-green-500" : "border-[#27272A]"
            }`}>
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-bold text-[#F5F5F0]">Variant A (Control)</h2>
                {winner === "A" && (
                  <span className="px-3 py-1 bg-green-500/20 text-green-400 text-sm font-medium rounded flex items-center gap-1">
                    <TrendingUp className="w-4 h-4" /> Winner
                  </span>
                )}
              </div>
              
              <div className="grid grid-cols-3 gap-4">
                <div className="text-center p-4 bg-[#0F0F0F] rounded">
                  <Eye className="w-5 h-5 text-[#3B82F6] mx-auto mb-2" />
                  <p className="text-2xl font-bold text-[#F5F5F0]">{variantA.views}</p>
                  <p className="text-xs text-[#A1A1AA]">Views</p>
                </div>
                <div className="text-center p-4 bg-[#0F0F0F] rounded">
                  <ShoppingCart className="w-5 h-5 text-[#10B981] mx-auto mb-2" />
                  <p className="text-2xl font-bold text-[#F5F5F0]">{variantA.conversions}</p>
                  <p className="text-xs text-[#A1A1AA]">Conversions</p>
                </div>
                <div className="text-center p-4 bg-[#0F0F0F] rounded">
                  <Target className="w-5 h-5 text-[#D4AF37] mx-auto mb-2" />
                  <p className="text-2xl font-bold text-[#F5F5F0]">{variantA.rate}%</p>
                  <p className="text-xs text-[#A1A1AA]">Conv. Rate</p>
                </div>
              </div>
            </div>

            {/* Variant B */}
            <div className={`bg-[#121212] border-2 rounded-lg p-6 ${
              winner === "B" ? "border-green-500" : "border-[#27272A]"
            }`}>
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-bold text-[#F5F5F0]">Variant B (Test)</h2>
                {winner === "B" && (
                  <span className="px-3 py-1 bg-green-500/20 text-green-400 text-sm font-medium rounded flex items-center gap-1">
                    <TrendingUp className="w-4 h-4" /> Winner
                  </span>
                )}
              </div>
              
              <div className="grid grid-cols-3 gap-4">
                <div className="text-center p-4 bg-[#0F0F0F] rounded">
                  <Eye className="w-5 h-5 text-[#3B82F6] mx-auto mb-2" />
                  <p className="text-2xl font-bold text-[#F5F5F0]">{variantB.views}</p>
                  <p className="text-xs text-[#A1A1AA]">Views</p>
                </div>
                <div className="text-center p-4 bg-[#0F0F0F] rounded">
                  <ShoppingCart className="w-5 h-5 text-[#10B981] mx-auto mb-2" />
                  <p className="text-2xl font-bold text-[#F5F5F0]">{variantB.conversions}</p>
                  <p className="text-xs text-[#A1A1AA]">Conversions</p>
                </div>
                <div className="text-center p-4 bg-[#0F0F0F] rounded">
                  <Target className="w-5 h-5 text-[#D4AF37] mx-auto mb-2" />
                  <p className="text-2xl font-bold text-[#F5F5F0]">{variantB.rate}%</p>
                  <p className="text-xs text-[#A1A1AA]">Conv. Rate</p>
                </div>
              </div>
            </div>
          </div>

          {/* Test Settings */}
          <div className="bg-[#121212] border border-[#27272A] rounded-lg p-6">
            <h3 className="font-semibold text-[#F5F5F0] mb-4">Test Settings</h3>
            <div className="grid md:grid-cols-3 gap-4">
              <div>
                <span className="text-[#a1a1aa] text-sm">Traffic Split</span>
                <p className="text-[#F5F5F0] font-medium">{test.traffic_split}% to Variant B</p>
              </div>
              <div>
                <span className="text-[#a1a1aa] text-sm">Created</span>
                <p className="text-[#F5F5F0] font-medium">{new Date(test.created_at).toLocaleDateString()}</p>
              </div>
              <div>
                <span className="text-[#a1a1aa] text-sm">Page</span>
                <Link to={`/${test.page_slug}`} className="text-[#D4AF37] hover:underline font-medium">
                  /{test.page_slug}
                </Link>
              </div>
            </div>
          </div>
        </div>
      </AdminLayout>
    );
  }

  // All tests list view
  return (
    <AdminLayout>
      <div className="p-6 lg:p-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-2xl font-bold text-[#F5F5F0]">A/B Tests</h1>
            <p className="text-[#A1A1AA]">Compare different page versions</p>
          </div>
          
          <button
            onClick={() => setShowCreate(true)}
            className="flex items-center gap-2 px-4 py-2 bg-[#D4AF37] text-[#050505] font-semibold hover:bg-[#B8943B] transition-colors rounded"
          >
            <Plus className="w-5 h-5" />
            New A/B Test
          </button>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="animate-spin rounded-full h-8 w-8 border-2 border-[#D4AF37] border-t-transparent"></div>
          </div>
        ) : (
          <div className="space-y-4">
            {tests.map(t => (
              <motion.div
                key={t.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="bg-[#121212] border border-[#27272A] rounded-lg p-6 hover:border-[#D4AF37]/50 transition-colors"
              >
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-lg font-semibold text-[#F5F5F0]">{t.name}</h3>
                    <p className="text-[#A1A1AA]">/{t.page_slug}</p>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className={`px-3 py-1 rounded text-sm font-medium ${
                      t.status === "active" 
                        ? "bg-green-500/20 text-green-400" 
                        : "bg-yellow-500/20 text-yellow-400"
                    }`}>
                      {t.status}
                    </span>
                    <Link
                      to={`/admin/ab-test/${t.id}`}
                      className="px-4 py-2 bg-[#27272A] text-[#F5F5F0] hover:bg-[#3F3F46] transition-colors rounded"
                    >
                      View Results
                    </Link>
                    <button
                      onClick={() => toggleTestStatus(t.id, t.status)}
                      className="p-2 bg-[#27272A] text-[#F5F5F0] hover:bg-[#3F3F46] transition-colors rounded"
                    >
                      {t.status === "active" ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
                    </button>
                    <button
                      onClick={() => deleteTest(t.id)}
                      className="p-2 bg-red-500/10 text-red-400 hover:bg-red-500/20 transition-colors rounded"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </motion.div>
            ))}
            {tests.length === 0 && (
              <div className="text-center py-20 bg-[#121212] border border-[#27272A] rounded-lg">
                <Target className="w-12 h-12 text-[#a1a1aa] mx-auto mb-4" />
                <p className="text-[#A1A1AA] mb-4">No A/B tests yet</p>
                <button
                  onClick={() => setShowCreate(true)}
                  className="px-4 py-2 bg-[#D4AF37] text-[#050505] font-semibold hover:bg-[#B8943B] transition-colors rounded"
                >
                  Create Your First Test
                </button>
              </div>
            )}
          </div>
        )}

        {/* Create Modal */}
        {showCreate && (
          <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4">
            <div className="bg-[#121212] border border-[#27272A] rounded-lg max-w-md w-full p-6">
              <h3 className="text-lg font-semibold text-[#F5F5F0] mb-4">Create A/B Test</h3>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm text-[#A1A1AA] mb-1">Test Name</label>
                  <input
                    type="text"
                    value={newTest.name}
                    onChange={(e) => setNewTest({ ...newTest, name: e.target.value })}
                    placeholder="Hero Image Test"
                    className="w-full px-4 py-2 bg-[#0F0F0F] border border-[#27272A] text-[#F5F5F0] rounded"
                  />
                </div>
                <div>
                  <label className="block text-sm text-[#A1A1AA] mb-1">Landing Page</label>
                  <select
                    value={newTest.page_slug}
                    onChange={(e) => setNewTest({ ...newTest, page_slug: e.target.value })}
                    className="w-full px-4 py-2 bg-[#0F0F0F] border border-[#27272A] text-[#F5F5F0] rounded"
                  >
                    <option value="">Select a page</option>
                    {landingPages.map(p => (
                      <option key={p.id} value={p.slug}>{p.title || p.slug}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm text-[#A1A1AA] mb-1">Traffic to Variant B: {newTest.traffic_split}%</label>
                  <input
                    type="range"
                    min="10"
                    max="90"
                    value={newTest.traffic_split}
                    onChange={(e) => setNewTest({ ...newTest, traffic_split: parseInt(e.target.value) })}
                    className="w-full accent-[#D4AF37]"
                  />
                </div>
              </div>

              <div className="flex gap-3 mt-6">
                <button
                  onClick={() => setShowCreate(false)}
                  className="flex-1 px-4 py-2 bg-[#27272A] text-[#F5F5F0] hover:bg-[#3F3F46] transition-colors rounded"
                >
                  Cancel
                </button>
                <button
                  onClick={createTest}
                  className="flex-1 px-4 py-2 bg-[#D4AF37] text-[#050505] font-semibold hover:bg-[#B8943B] transition-colors rounded"
                >
                  Create Test
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default ABTestMonitor;
